import React, { useCallback, useEffect, useState } from "react"
import { Outlet } from "react-router-dom"
import Topbar from "./Topbar.jsx"
import Sidebar from "./Sidebar.jsx"
import useApi from "../../hooks/useApi.js"

// Shell-level metrics fetch. Piggybacks on /shop/metrics (no dedicated
// endpoint) and exposes the full payload to child pages via Outlet
// context. Sidebar reads `pending_proposals` for the nav pill;
// ProposalsPage reads `has_traffic_for_auto_generate` for the gate UI.
// Child pages call refresh via the Outlet context after actions that
// change either count (approve/dismiss/generate).
function useShellMetrics() {
  const api = useApi()
  const [metrics, setMetrics] = useState(null)

  const refresh = useCallback(async () => {
    try {
      const m = await api.get("/api/v1/shop/metrics")
      setMetrics(m || null)
    } catch { /* metrics are best-effort on the shell */ }
  }, [ api ])

  useEffect(() => { refresh() }, [ refresh ])

  return { metrics, refresh }
}

export default function DashboardShell({ t, shopState }) {
  const { metrics, refresh: refreshMetrics } = useShellMetrics()
  const pendingProposalsCount = metrics?.pending_proposals || 0

  // Proposals page + others call this after approve/dismiss/generate
  // to keep the sidebar pill AND the gate state on proposals in sync.
  const refreshPendingProposals = refreshMetrics

  return (
    <div className="min-h-screen bg-surface-page">
      <Topbar t={t} shop={shopState.shop} />
      <div
        className="grid"
        style={{ gridTemplateColumns: "200px minmax(0, 1fr)", minHeight: "calc(100vh - 52px)" }}
      >
        <Sidebar
          t={t}
          pendingProposalsCount={pendingProposalsCount}
          scriptInstalled={!!shopState.shop?.script_installed}
          conversionDefined={(shopState.shop?.conversion_rules || []).some((r) => r.active)}
        />
        <main className="px-9 py-8">
          <div className="max-w-[1100px]">
            <Outlet context={{
              shop: shopState.shop,
              shopState,
              metrics,
              refreshMetrics,
              refreshPendingProposals
            }} />
          </div>
        </main>
      </div>
    </div>
  )
}
